import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalSidebar from '../components/GlobalSidebar';
import GlobalEmptyState from '../components/GlobalEmptyState';
import { GlobalSortableTh } from '../components/GlobalSortableTh';
import { GlobalButton } from '../components/GlobalButton';
import { useTableSort } from '../hooks/useTableSort';
import { StatCard } from '../components/StatCard';
import { SortableStatGrid } from '../components/SortableStatGrid';
import { ViewPage, ViewSectionHeader } from '../components/ViewPage';
import { ViewLayoutToggle } from '../components/ViewLayoutToggle';
import { ViewSections, type ViewSection } from '../components/ViewSections';
import { useViewLayoutMode } from '../hooks/useViewLayoutMode';
import { InvestmentCaseForm } from '../components/InvestmentCaseForm';
import { RatingMeter } from '../components/RatingMeter';
import { useAppData } from '../context/AppDataContext';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeProvider';
import type { InvestmentCase } from '../types';
import {
  investmentCaseAverageScore,
  investmentCaseVerdict,
} from '../utils/investmentCase';
import { buildInvestmentCasesKpiExplains } from '../lib/kpiExplainBuilders';
import { flagSvgUrlForCountryCode } from '../utils/investmentCompanyUi';
import { Favicon } from '../utils/favicon';
import { newId } from '../utils/id';

type CaseSortKey =
  | 'company'
  | 'founderAmbition'
  | 'timeToRevenue'
  | 'exitExperience'
  | 'founderSkillset'
  | 'marketSize'
  | 'score'
  | 'verdict';

export function InvestmentCasesView() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { mode: layoutMode, toggle: toggleLayout } = useViewLayoutMode();
  const { user } = useUser();
  const userId = user?.uid ?? '';
  const { investmentCases, setInvestmentCases } = useAppData();
  const [addOpen, setAddOpen] = useState(false);

  const { sortKey, sortDirection, toggleSort, sortRows } = useTableSort<CaseSortKey>('score', 'desc');

  const sorted = useMemo(() => {
    return sortRows(investmentCases, {
      company: (c) => c.name,
      founderAmbition: (c) => c.founderAmbition,
      timeToRevenue: (c) => c.timeToRevenue,
      exitExperience: (c) => c.exitExperience,
      founderSkillset: (c) => c.founderSkillset,
      marketSize: (c) => c.marketSize,
      score: (c) => investmentCaseAverageScore(c),
      verdict: (c) => (investmentCaseVerdict(c) === 'INVEST' ? 1 : 0),
    });
  }, [investmentCases, sortRows]);

  const summary = useMemo(() => {
    const total = investmentCases.length;
    const investCount = investmentCases.filter(
      (c) => investmentCaseVerdict(c) === 'INVEST',
    ).length;
    const avgScore =
      total > 0
        ? investmentCases.reduce((sum, c) => sum + investmentCaseAverageScore(c), 0) / total
        : 0;

    let topCase: { name: string; score: number } | null = null;
    for (const c of investmentCases) {
      const score = investmentCaseAverageScore(c);
      if (!topCase || score > topCase.score) {
        topCase = { name: c.name, score };
      }
    }

    const marketCount = new Set(
      investmentCases
        .map((c) => c.countryCode?.trim().toLowerCase())
        .filter((code): code is string => Boolean(code)),
    ).size;

    return { total, investCount, avgScore, topCase, marketCount };
  }, [investmentCases]);

  const summaryHints = useMemo(() => {
    const { total, investCount, avgScore, topCase, marketCount } = summary;
    return {
      pipeline:
        total > 0
          ? `${total} ${total === 1 ? 'case' : 'cases'} scored across your dealflow`
          : 'Cases appear here as you score opportunities',
      invest:
        total > 0
          ? `${investCount} of ${total} clear the average ≥ 4 bar`
          : 'Verdicts use the average of the five ratings',
      avg:
        total > 0
          ? avgScore >= 4
            ? 'Pipeline leans toward INVEST on average'
            : 'Pipeline leans toward DON’T on average'
          : 'Mean of every case’s five-dimension average',
      top: topCase
        ? `${topCase.name} · ${topCase.score.toFixed(2)} avg`
        : 'Your strongest opportunity surfaces here',
      markets:
        marketCount > 0
          ? `${marketCount} ${marketCount === 1 ? 'country' : 'countries'} in the pipeline`
          : 'Set country flags on cases to track geography',
    };
  }, [summary]);

  const kpiExplains = useMemo(
    () => buildInvestmentCasesKpiExplains(investmentCases, summary),
    [investmentCases, summary],
  );

  function saveNewCase(next: InvestmentCase) {
    const id = next.id?.trim() ? next.id : newId();
    const merged = { ...next, id };
    setInvestmentCases((prev) => [...prev, merged]);
    setAddOpen(false);
    navigate(`/investment-cases/${id}`);
  }

  const toolbarActions = (
    <>
      <ViewLayoutToggle mode={layoutMode} onToggle={toggleLayout} />
      <GlobalButton
        type="button"
        variant="secondary"
        onClick={() => setAddOpen(true)}
        icon={
          <img
            src={isDarkMode ? '/assets/icons/plus.circle.svg' : '/assets/icons/plus.circle.black.svg'}
            alt=""
            width={16}
            height={16}
          />
        }
      >
        Add case
      </GlobalButton>
    </>
  );

  const summarySection = (
    <>
      <ViewSectionHeader
        eyebrow="Dealflow"
        title="Summary"
        subtitle="Pipeline volume, average score, and how many cases clear the invest bar."
      />

      <SortableStatGrid
        viewId="investment-cases"
        className="grid-stats grid-stats-2"
        cards={[
          {
            id: 'pipeline',
            pinned: true,
            label: 'Pipeline',
            node: (
              <StatCard
                label="Pipeline"
                emoji="📋"
                value={`${summary.total} Cases`}
                hint={summaryHints.pipeline}
                kpiExplain={kpiExplains.pipeline}
              />
            ),
          },
          {
            id: 'invest-ready',
            label: 'Invest-ready',
            node: (
              <StatCard
                label="Invest-ready"
                emoji="✅"
                value={`${summary.investCount}/${summary.total} Cases`}
                hint={summaryHints.invest}
                kpiExplain={kpiExplains['invest-ready']}
              />
            ),
          },
          {
            id: 'avg-score',
            label: 'Average score',
            node: (
              <StatCard
                label="Average score"
                emoji="⭐"
                value={summary.total > 0 ? summary.avgScore.toFixed(2) : '—'}
                hint={summaryHints.avg}
                kpiExplain={kpiExplains['avg-score']}
              />
            ),
          },
          {
            id: 'top-case',
            label: 'Top case',
            node: (
              <StatCard
                label="Top case"
                emoji="👑"
                value={summary.topCase ? summary.topCase.score.toFixed(2) : '—'}
                hint={summaryHints.top}
                kpiExplain={kpiExplains['top-case']}
              />
            ),
          },
          {
            id: 'markets',
            label: 'Markets',
            node: (
              <StatCard
                label="Markets"
                emoji="🌍"
                value={summary.marketCount > 0 ? String(summary.marketCount) : '—'}
                hint={summaryHints.markets}
                kpiExplain={kpiExplains.markets}
              />
            ),
          },
        ]}
      />
    </>
  );

  const dealflowSection = (
    <>
      <ViewSectionHeader
        eyebrow="Dealflow"
        title="Cases"
        subtitle="Every opportunity with five DD ratings and an INVEST or DON'T verdict."
      />

      <section>
        {sorted.length === 0 ? (
          <div className="empty-state">
            <p className="muted">Add your first deal to track DD scores across ambition, market size, and verdict.</p>
            <GlobalEmptyState buttonText="Add a case" onButtonClick={() => setAddOpen(true)} />
          </div>
        ) : (
          <div className="table-card">
          <div className="table-scroll">
            <table className="data-table investmentCasesTable">
              <thead>
                <tr>
                  <GlobalSortableTh sortKey="company" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Company
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="founderAmbition" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Founder ambition
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="timeToRevenue" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Time to revenue
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="exitExperience" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Exit experience
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="founderSkillset" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Founder skillset
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="marketSize" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Market size
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="score" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Score
                  </GlobalSortableTh>
                  <GlobalSortableTh sortKey="verdict" activeKey={sortKey} direction={sortDirection} onSort={toggleSort}>
                    Verdict
                  </GlobalSortableTh>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c) => {
                  const verdict = investmentCaseVerdict(c);
                  const avg = investmentCaseAverageScore(c);
                  const flagUrl = flagSvgUrlForCountryCode(c.countryCode);
                  return (
                    <tr
                      key={c.id}
                      className="click-row"
                      onClick={() => navigate(`/investment-cases/${c.id}`)}
                    >
                      <td>
                        <div className="companyCell">
                          {c.websiteUrl ? (
                            <Favicon
                              websiteUrl={c.websiteUrl}
                              size={20}
                              alt={`${c.name} site`}
                              className="companyFavicon"
                            />
                          ) : (
                            <span
                              className="companyFavicon"
                              style={{
                                display: 'inline-block',
                                width: 20,
                                height: 20,
                                borderRadius: 6,
                                background: 'var(--global-separator-color)',
                              }}
                              aria-hidden
                            />
                          )}
                          <span>{c.name}</span>
                          {flagUrl ? (
                            <img
                              src={flagUrl}
                              alt=""
                              className="ticketCellFlag investmentCasesFlag"
                              width={15}
                              height={13}
                              loading="lazy"
                              decoding="async"
                            />
                          ) : null}
                        </div>
                      </td>
                      <td className="investmentCasesMeterCell">
                        <RatingMeter value={c.founderAmbition} label={`Founder ambition ${c.founderAmbition} of 6`} />
                      </td>
                      <td className="investmentCasesMeterCell">
                        <RatingMeter value={c.timeToRevenue} label={`Time to revenue ${c.timeToRevenue} of 6`} />
                      </td>
                      <td className="investmentCasesMeterCell">
                        <RatingMeter value={c.exitExperience} label={`Exit experience ${c.exitExperience} of 6`} />
                      </td>
                      <td className="investmentCasesMeterCell">
                        <RatingMeter value={c.founderSkillset} label={`Founder skillset ${c.founderSkillset} of 6`} />
                      </td>
                      <td className="investmentCasesMeterCell">
                        <RatingMeter value={c.marketSize} label={`Market size ${c.marketSize} of 6`} />
                      </td>
                      <td className="investmentCasesScoreCell">{avg.toFixed(2)}</td>
                      <td>
                        <span
                          className={
                            verdict === 'INVEST' ? 'verdictPill verdictPill--invest' : 'verdictPill verdictPill--dont'
                          }
                        >
                          {verdict}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </section>
    </>
  );

  const sections: ViewSection[] = [
    { id: 'summary', label: 'Summary', content: summarySection },
    { id: 'dealflow', label: 'Dealflow', content: dealflowSection },
  ];

  return (
    <ViewPage title="Dealflow" actions={toolbarActions}>
      <ViewSections mode={layoutMode} sections={sections} />

      <GlobalSidebar isOpen={addOpen} onClose={() => setAddOpen(false)}>
        <div className="sidebarFormStack">
          <h2 className="sidebarFormTitle">New case</h2>
          <InvestmentCaseForm
            userId={userId}
            onSubmit={saveNewCase}
            onCancel={() => setAddOpen(false)}
          />
        </div>
      </GlobalSidebar>
    </ViewPage>
  );
}
